"use client";

import { useEffect, useRef } from "react";
import { useChartStore3 } from "@/vybe-trader-client/chart/stores/useChartStore3";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";
import { useSymbolsStore } from "@/vybe-trader-client/symbols/stores/useSymbolsStore";
import { useAccountsStore } from "@/lib/accounts/store";
import {
  useDesktopLayoutStore,
  type DesktopChartLayoutMode,
} from "@/lib/desktop-layout/store";
import type { ChartPanelId } from "@/vybe-trader-client/chart/stores/chartPanelTypes";
import type { Symbol } from "@/vybe-trader-client/symbols/types";
import { currentDeskAccountKey } from "./accountKey";
import { readDesk } from "./storage";
import type { ChartDeskSlot } from "./types";
import { useChartDeskRestoreStore } from "./restore-store";
import { setDeskRestoring } from "./write";

/** Which panels a layout mode actually renders — never restore into a hidden slot. */
function visiblePanels(
  isDesktop: boolean,
  mode: DesktopChartLayoutMode,
): ChartPanelId[] {
  if (!isDesktop) return [0];
  switch (mode) {
    case "quad":
      return [0, 1, 2, 3];
    case "two-horizontal":
    case "two-vertical":
      return [0, 1];
    case "single":
    default:
      return [0];
  }
}

/**
 * Proposal 74 — replay the stored chart desk (symbol + timeframe per panel) once
 * per app load, as soon as the symbol universe can resolve it.
 *
 * Mirrors the seeding pattern the detached chart window already uses
 * (`app/routes/chart-popup/page.tsx`): guard with a ref, wait for the symbol
 * stores, then set timeframe before symbol so the first data request is made at
 * the right resolution. Not mounted on the popup route — an explicit `?symbol=`
 * always wins over the snapshot.
 */
export function useRestoreChartDesk(isDesktop: boolean): void {
  const finishedRef = useRef(false);
  const donePanelsRef = useRef<Set<ChartPanelId>>(new Set());

  const allSubscribed = useSubscribedSymbolsStore((s) => s.allSubscribed);
  const symbolsByCurrency = useSymbolsStore((s) => s.byCurrency);
  const symbolsLoadingStatus = useSymbolsStore((s) => s.loadingStatus);
  const activeAccountId = useAccountsStore((s) => s.activeAccountId);
  const chartLayoutMode = useDesktopLayoutStore((s) => s.chartLayoutMode);

  useEffect(() => {
    if (finishedRef.current) return;

    const snapshot = readDesk();
    if (!snapshot) {
      finishedRef.current = true;
      return;
    }

    // No active account yet — keep waiting. A *different* account means this
    // desk is not ours to restore.
    const accountKey = currentDeskAccountKey();
    if (!accountKey) return;
    if (accountKey !== snapshot.accountKey) {
      finishedRef.current = true;
      return;
    }

    const resolve = (currency: string): Symbol | null => {
      const fromSubscribed = allSubscribed.find(
        (entry) => entry.symbol.currency === currency,
      )?.symbol;
      if (fromSubscribed) return fromSubscribed;
      return symbolsByCurrency.get(currency) ?? null;
    };

    // Only once the symbol sync has COMPLETED may a currency that still doesn't
    // resolve be treated as delisted. The universe streams in batches, so a
    // non-empty map is not "ready" — declaring readiness on the first partial
    // batch dropped every symbol that happened to arrive in a later batch.
    const universeReady = symbolsLoadingStatus === "complete";

    const wanted: Array<{ panelId: ChartPanelId; slot: ChartDeskSlot }> = [];
    for (const panelId of visiblePanels(isDesktop, chartLayoutMode)) {
      const slot = snapshot.slots[panelId];
      if (slot && !donePanelsRef.current.has(panelId)) {
        wanted.push({ panelId, slot });
      }
    }

    if (wanted.length === 0) {
      finishedRef.current = true;
      return;
    }

    // Tell the still-empty panels what they are waiting for.
    const restoreStore = useChartDeskRestoreStore.getState();
    restoreStore.markPending(
      Object.fromEntries(wanted.map(({ panelId, slot }) => [panelId, slot])),
    );

    setDeskRestoring(true);
    try {
      const store = useChartStore3.getState();
      for (const { panelId, slot } of wanted) {
        const symbol = resolve(slot.symbol);
        if (!symbol) {
          if (universeReady) {
            // Delisted between sessions — stop waiting on it.
            donePanelsRef.current.add(panelId);
            useChartDeskRestoreStore.getState().clearPending(panelId);
          }
          continue;
        }

        donePanelsRef.current.add(panelId);
        useChartDeskRestoreStore.getState().clearPending(panelId);

        // Never clobber a symbol the user picked while we were still waiting.
        if (useChartStore3.getState().slots[panelId].selectedSymbol) continue;

        store.setSelectedTimeframe(slot.timeframe, panelId);
        store.setSelectedSymbol(symbol, panelId);
      }
    } finally {
      setDeskRestoring(false);
    }

    if (wanted.every(({ panelId }) => donePanelsRef.current.has(panelId))) {
      finishedRef.current = true;
    }
  }, [
    allSubscribed,
    symbolsByCurrency,
    symbolsLoadingStatus,
    activeAccountId,
    chartLayoutMode,
    isDesktop,
  ]);
}
